import * as d3 from "d3";
import { observer } from "mobx-react-lite";

import { useEffect, useState } from "react";
import { getEvents } from "../../utils/api";
import * as moment from "moment";
import "./EventChart.css";
let x;
let brush;

function EventChart({
  store,
  userId,
  startDate,
  endDate,
  selected,
  setSelected,
  updateBounds,
}) {
  const margin = { top: 0, right: 0, bottom: 20, left: 0 },
    width = 960 - margin.left - margin.right,
    height = 40 - margin.top - margin.bottom,
    color = "#676bcf";

  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [updated, setUpdated] = useState(0);

  const getXAxis = (ref) => {
    const xAxis = d3.axisBottom(x);
    d3.select(ref).call(xAxis);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (userId && startDate && endDate) {
        const isoStart = moment(startDate).toDate().toISOString();
        const isoEnd = moment(endDate).add(1, "day").toDate().toISOString();
        const events = await getEvents(userId, isoStart, isoEnd);
        store.events = events.data;

        const data = events.data.map((x) => {
          return {
            startDate: new Date(x.eventstart),
            endDate: new Date(x.eventend),
            duration: x.eventduration,
            eventtype: x.eventtype,
            reportnum: x.reportnum,
          };
        });

        data.sort((x) => x.startDate.getTime() - x.endDate.getTime());
        x = d3
          .scaleTime()
          .domain(d3.extent(data, (d) => d.startDate))
          .range([10, width - 10]);
        setData(data);
        setLoaded(true);
      }
    };
    fetchData();
  }, [userId, startDate, endDate]);

  const updateChart = (event) => {
    const extent = event.selection;
    if (!extent) {
      return;
    } else {
      x.domain([x.invert(extent[0]), x.invert(extent[1])]);
      const xAxis = d3.selectAll(`.xAxisEvent`);
      xAxis.transition().duration(1000).call(d3.axisBottom(x));
      d3.select(`#brush`).call(event.target.move, null);
      store.setBounds(x.domain());
      updateBounds(x.domain());
      setUpdated(updated + 1);
    }
  };

  brush = d3.brushX().extent([
    [0, 0],
    [width, height],
  ]);

  d3.select(`#brush`).call(brush);
  brush.on("end", updateChart);

  const dblClick = () => {
    x.domain(d3.extent(data, (d) => d.startDate));
    const xAxis = d3.selectAll(`.xAxisEvent`);
    xAxis.transition().duration(1000).call(d3.axisBottom(x));
    store.setBounds([null, null]);
    updateBounds(x.domain());
    setSelected(null, null);
    setUpdated(updated + 1);
  };

  const mouseOver = (e, d) => {
    let tooltip = d3.select(`#tooltipEvent`);
    tooltip.transition().duration("50").style("opacity", 1);
    let text = `Event Type: ${d.eventtype}<br/>
      Event Start: ${moment(d.eventstart).format(
        "MMMM Do YYYY, h:mm:ss a"
      )}<br/>
      Event End: ${moment(d.eventend).format("MMMM Do YYYY, h:mm:ss a")}<br/>`;
    tooltip
      .html(`${text}`)
      .style("left", e.pageX + 15 + "px")
      .style("top", e.pageY - 55 + "px")
      .style("padding", "20px")
      .style("z-index", 1);
  };

  const mouseOut = (e) => {
    let tooltip = d3.select(`#tooltipEvent`);
    tooltip
      .transition()
      .duration("50")
      .style("opacity", 0)
      .style("z-index", -1);
  };

  const eventClicked = (e, d) => {
    if (selected) {
      setSelected(null, null);
    } else {
      setSelected(d.reportnum, d);
    }
  };

  return (
    <div className="wrapper">
      <div className="tooltip-donut" id={`tooltipEvent`}></div>
      <svg
        viewBox={`0 0 ${width + margin.left + margin.right} 
        ${height + margin.top + margin.bottom}`}
      >
        <g id="event" onDoubleClick={() => dblClick()}>
          <g id="brush"></g>

          {loaded && (
            <g
              className="axis xAxisEvent"
              ref={getXAxis}
              transform={`translate(0,${height})`}
            />
          )}
          {loaded &&
            data.map((item, index) => {
              return (
                <g key={`event-chart-${index}`}>
                  <rect
                    className={
                      selected === item.reportnum ? "circle selected" : "circle"
                    }
                    rx="3"
                    x={x(item.startDate)}
                    y={7}
                    width={Math.max(6, x(item.endDate) - x(item.startDate))}
                    height={6}
                    strokeWidth={0}
                    stroke={color}
                    style={{ transition: "ease-out .1s" }}
                    onMouseOver={(e) => mouseOver(e, item)}
                    onMouseOut={mouseOut}
                    onClick={(e) => eventClicked(e, item)}
                  />
                </g>
              );
            })}
        </g>
      </svg>
    </div>
  );
}

export default observer(EventChart);
